/* © CNES */
import moment from 'moment';
import TileGrid from 'ol/tilegrid/TileGrid';
import { Feature } from '../../../models/Feature';
import { ItemFilter } from '../../../models/Filter';
import { END_DATE_TIME, START_DATE_TIME } from '../Constants';

/**
 * format Time Parameter.
 * @param isFiltersVisible is Filters View is visible.
 * @param filterData filterData context.
 * @param searchedFeatures searched Features to format Time from.
 * @returns string the formated Time Parameter for geoserver.
 */
export const formatTimeParameter = (
  isFiltersVisible: boolean,
  filterData: ItemFilter,
  searchedFeatures: Feature[]
) => {
  const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSS';
  let endDate: string;
  let startDate: string;

  if (isFiltersVisible) {
    let momentEndDate = filterData[END_DATE_TIME]
      ? moment(filterData[END_DATE_TIME]?.slice(0, -1))
      : moment('1960-01-01T00:00:00.000');
    let momentStartDate = filterData[START_DATE_TIME]
      ? moment(filterData[START_DATE_TIME]?.slice(0, -1))
      : moment();

    momentEndDate.milliseconds(0);
    momentStartDate.milliseconds(0);
    startDate = momentEndDate.format(dateFormat);
    endDate = momentStartDate.format(dateFormat);
  } else {
    endDate = searchedFeatures?.reduce((acc, curr) => {
      if (moment(curr.properties[END_DATE_TIME].slice(0, -1)).isAfter(acc)) {
        let momentDate = moment(curr.properties[END_DATE_TIME].slice(0, -1));
        acc = momentDate.format(dateFormat);
      }
      return acc;
    }, moment('1960-01-01T00:00:00.000').format(dateFormat));
    startDate = searchedFeatures?.reduce((acc, curr) => {
      if (moment(curr.properties[START_DATE_TIME].slice(0, -1)).isBefore(acc)) {
        let momentDate = moment(curr.properties[START_DATE_TIME].slice(0, -1));
        momentDate.milliseconds(0);
        acc = momentDate.format(dateFormat);
      }
      return acc;
    }, moment().format(dateFormat));
  }

  return moment(endDate).isSame(startDate)
    ? `${startDate}Z`
    : `${startDate}Z/${endDate}Z`;
};

/**
 * Format any Cover Parameter for geoserver request.
 * @param filterData filterData context.
 * @returns string the formated Cover Parameter.
 */
export const formatGenericCoverParameter = (
  filterDataItem: string | string[] | null
) => {
  let lowRangeValue = -1;
  let highRangeValue = 100;

  if (filterDataItem && typeof filterDataItem === 'object') {
    [lowRangeValue, highRangeValue] = filterDataItem.map(
      (value) => parseInt(value) ?? -1
    );
  }

  return `${lowRangeValue}/${highRangeValue}`;
};

const tileSourceGenerator = (
  baseUrl: string,
  tileSourceInfo: {
    sourceUrl: string;
    LAYERS: string;
    hasSnowCover: boolean;
    hasCloudCover: boolean;
    hasWaterCover: boolean;
    hasNoDataPixel: boolean;
  },
  formatTimeParameter: string,
  CQL_FILTERS: { name: string; CQLFilter: string }[],
  formatCloudCoverParameter: string,
  formatSnowCoverParameter: string,
  formatWaterCoverParameter: string,
  formatNoDataPixelCoverParameter: string,
  bbox: string | undefined,
  tileGrid: TileGrid
) => {
  return {
    url: `${baseUrl}${tileSourceInfo.sourceUrl}`,
    params: {
      VERSION: '1.1.0',
      LAYERS: tileSourceInfo.LAYERS,
      // styles: '',
      FORMAT: 'image/png',
      time: formatTimeParameter,
      ...(!!CQL_FILTERS.find((filter) => filter.name === tileSourceInfo.LAYERS)
        ?.CQLFilter.length && {
        CQL_FILTER: CQL_FILTERS.find(
          (filter) => filter.name === tileSourceInfo.LAYERS
        )?.CQLFilter,
      }),
      ...(tileSourceInfo.hasCloudCover && {
        DIM_cloudcover: formatCloudCoverParameter,
      }),
      ...(tileSourceInfo.hasSnowCover && {
        DIM_snowcover: formatSnowCoverParameter,
      }),
      ...(tileSourceInfo.hasWaterCover && {
        DIM_watercover: formatWaterCoverParameter,
      }),
      ...(tileSourceInfo.hasNoDataPixel && {
        DIM_nodata_pixel: formatNoDataPixelCoverParameter,
      }),
      ...(!!bbox && { BBOX: bbox }),
      maxFeatures: 1000,
    },
    serverType: 'geoserver',
    tileGrid,
  };
};

/**
 * tileSource Reference.
 */
export const tileSourceReference = [
  {
    sourceUrl: `/geoserver/peps/wms`,
    LAYERS: 'gdh_s1',
    hasSnowCover: false,
    hasCloudCover: false,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterS1',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_s2_l2a3a',
    hasSnowCover: false,
    hasCloudCover: true,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterSentinel2',
  },
  {
    sourceUrl: `/geoserver/peps/wms`,
    LAYERS: 'peps:gdh_s2_l1c',
    hasSnowCover: false,
    hasCloudCover: true,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterSentinel2',
  },
  {
    sourceUrl: `/geoserver/histo/wms`,
    LAYERS: 'histo:gdh_spot',
    hasSnowCover: true,
    hasCloudCover: true,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterSpot_reflectance',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_venus',
    hasSnowCover: false,
    hasCloudCover: true,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterVenus',
  },
  {
    sourceUrl: `/geoserver/histo/wms`,
    LAYERS: 'histo:gdh_spotdem',
    hasSnowCover: false,
    hasCloudCover: false,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterSpot_reflectance',
  },
  {
    sourceUrl: `/geoserver/histo/wms`,
    LAYERS: 'histo:gdh_landsat57',
    hasSnowCover: false,
    hasCloudCover: true,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterLandSat',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_landsat8',
    hasSnowCover: false,
    hasCloudCover: true,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterLandSat',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_l2bl3b',
    hasSnowCover: true,
    hasCloudCover: true,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterGdhSnow',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_oso',
    hasSnowCover: false,
    hasCloudCover: false,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterOSO',
  },
  {
    sourceUrl: `/geoserver/histo/wms`,
    LAYERS: 'histo:gdh_postel',
    hasSnowCover: false,
    hasCloudCover: false,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterPostel',
  },
  {
    sourceUrl: `/geoserver/trishna/wms`,
    LAYERS: 'trishna:gdh_trishna',
    hasSnowCover: false,
    hasCloudCover: true,
    hasWaterCover: true,
    hasNoDataPixel: true,
    CQLFilterName: 'CQLFilterTrishna',
  },
  {
    sourceUrl: `/geoserver/co3d/wms`,
    LAYERS: 'co3d:gdh_co3d',
    hasSnowCover: false,
    hasCloudCover: true,
    hasWaterCover: false,
    hasNoDataPixel: false,
    CQLFilterName: 'CQLFilterCo3d',
  }
];

/**
 * generate TileSource
 * @param baseUrl url string for Tilesource.
 * @param formatTimeParameter function
 * @param CQL_FILTERS CQL_FILTERS
 * @param formatCloudCoverParameter function
 * @param formatSnowCoverParameter function
 * @param bbox bbox
 * @param tileGrid tileGrid
 * @returns TileSource
 */
export const generateTileSource = (
  baseUrl: string,
  formatTimeParameter: string,
  CQL_FILTERS: { name: string; CQLFilter: string; showLayer: boolean }[],
  formatCloudCoverParameter: string,
  formatSnowCoverParameter: string,
  formatWaterCoverParameter: string,
  formatNoDataPixelCoverParameter: string,
  bbox: string | undefined,
  tileGrid: TileGrid
) => {
  return tileSourceReference.map((sourceInfo) => {
    return tileSourceGenerator(
      baseUrl,
      sourceInfo,
      formatTimeParameter,
      CQL_FILTERS,
      formatCloudCoverParameter,
      formatSnowCoverParameter,
      formatWaterCoverParameter,
      formatNoDataPixelCoverParameter,
      bbox,
      tileGrid
    );
  });
};
